/**
 * Управление кэшем: snapshots / repo-checkouts / diff-cache.
 * Tabs + per-row purge + бэйдж rebuild (только snapshots).
 */

import {
    ActionIcon, Alert, Button, Card, Center, Group, Loader, Menu, Modal, Stack, Table,
    Tabs, Text, TextInput, Title,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
    fetchDiffsCache, fetchReposCache, fetchSnapshotsCache,
    purgeDiffsCache, purgeReposCache, purgeSnapshotsCache, rebuildSnapshot,
    type DiffCacheEntry, type RepoCheckoutEntry, type SnapshotCacheEntry,
} from '../../lib/api-admin';
import { DESKTOP_QUERY } from '../../lib/breakpoints';
import { fmtBytes, fmtRelative } from './format';

type Tab = 'snapshots' | 'repos' | 'diffs';

export function AdminCachePage(): JSX.Element {
    const [tab, setTab] = useState<Tab>('snapshots');
    const [owner, setOwner] = useState('');
    const [repo, setRepo] = useState('');

    return (
        <Stack gap="md">
            <Title order={2}>Кэш</Title>
            <Group gap="sm" wrap="wrap">
                <TextInput placeholder="owner" value={owner} onChange={e => setOwner(e.currentTarget.value)} w={160} />
                <TextInput placeholder="repo"  value={repo}  onChange={e => setRepo(e.currentTarget.value)}  w={160} />
            </Group>
            <Tabs value={tab} onChange={v => setTab((v ?? 'snapshots') as Tab)}>
                <Tabs.List>
                    <Tabs.Tab value="snapshots">Snapshots v2</Tabs.Tab>
                    <Tabs.Tab value="repos">Repo checkouts</Tabs.Tab>
                    <Tabs.Tab value="diffs">Diff cache</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="snapshots" pt="md"><SnapshotsTab owner={owner} repo={repo} /></Tabs.Panel>
                <Tabs.Panel value="repos"     pt="md"><ReposTab     owner={owner} repo={repo} /></Tabs.Panel>
                <Tabs.Panel value="diffs"     pt="md"><DiffsTab     owner={owner} repo={repo} /></Tabs.Panel>
            </Tabs>
        </Stack>
    );
}

// ─── snapshots ────────────────────────────────────────────────────────

function SnapshotsTab({ owner, repo }: { owner: string; repo: string }): JSX.Element {
    const qc = useQueryClient();
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const [purgeAllOpen, purgeAllH] = useDisclosure(false);

    const q = useQuery({
        queryKey: ['admin-cache-snapshots', owner, repo],
        queryFn:  () => fetchSnapshotsCache({ owner: owner || undefined, repo: repo || undefined }),
        retry: false,
    });
    const purgeOne = useMutation({
        mutationFn: (e: SnapshotCacheEntry) => purgeSnapshotsCache({ owner: e.owner, repo: e.repo, sha: e.sha }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-cache-snapshots'] }),
    });
    const rebuild = useMutation({
        mutationFn: (e: SnapshotCacheEntry) => rebuildSnapshot({ owner: e.owner, repo: e.repo, sha: e.sha }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-cache-snapshots'] }),
    });
    const purgeAll = useMutation({
        mutationFn: () => purgeSnapshotsCache({ all: true }),
        onSuccess:  () => { qc.invalidateQueries({ queryKey: ['admin-cache-snapshots'] }); purgeAllH.close(); },
    });

    return (
        <Stack gap="sm">
            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <>
                    <Group justify="space-between" wrap="wrap">
                        <Text fz="sm" c="dimmed">Записей: {q.data.entries.length}, размер: {fmtBytes(q.data.entries.reduce((s, e) => s + e.size, 0))}</Text>
                        <Button color="red" variant="subtle" size="xs" onClick={purgeAllH.open}>Очистить весь раздел</Button>
                    </Group>
                    {isDesktop
                        ? (
                            <Card withBorder padding={0}>
                                <Table fz="sm" striped highlightOnHover verticalSpacing="xs">
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Owner/Repo</Table.Th>
                                            <Table.Th>SHA</Table.Th>
                                            <Table.Th>Subpath</Table.Th>
                                            <Table.Th>Размер</Table.Th>
                                            <Table.Th>Обновлено</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {q.data.entries.map(e => (
                                            <Table.Tr key={`${e.owner}/${e.repo}/${e.sha}/${e.subpath}`}>
                                                <Table.Td>{e.owner}/{e.repo}</Table.Td>
                                                <Table.Td><Text fz="xs" ff="monospace">{e.sha.slice(0, 10)}</Text></Table.Td>
                                                <Table.Td><Text fz="xs">{e.subpath || '(root)'}</Text></Table.Td>
                                                <Table.Td>{fmtBytes(e.size)}</Table.Td>
                                                <Table.Td><Text fz="xs">{fmtRelative(e.mtimeMs)}</Text></Table.Td>
                                                <Table.Td>
                                                    <RowActions
                                                        onRebuild={() => rebuild.mutate(e)}
                                                        onPurge={() => { if (confirm(`Удалить ${e.owner}/${e.repo}@${e.sha.slice(0, 7)}?`)) purgeOne.mutate(e); }}
                                                    />
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Card>
                        )
                        : (
                            <Stack gap="xs">
                                {q.data.entries.map(e => (
                                    <Card withBorder padding="sm" key={`${e.owner}/${e.repo}/${e.sha}/${e.subpath}`}>
                                        <Group justify="space-between" align="start" wrap="nowrap">
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <Text fw={500} truncate>{e.owner}/{e.repo}</Text>
                                                <Text fz="xs" c="dimmed" ff="monospace">{e.sha.slice(0, 10)} {e.subpath && `· ${e.subpath}`}</Text>
                                                <Text fz="xs" c="dimmed">{fmtBytes(e.size)} · {fmtRelative(e.mtimeMs)}</Text>
                                            </div>
                                            <RowActions
                                                onRebuild={() => rebuild.mutate(e)}
                                                onPurge={() => { if (confirm(`Удалить ${e.owner}/${e.repo}@${e.sha.slice(0, 7)}?`)) purgeOne.mutate(e); }}
                                            />
                                        </Group>
                                    </Card>
                                ))}
                            </Stack>
                        )
                    }
                </>
            )}
            {rebuild.error && <Alert color="red">rebuild: {(rebuild.error as Error).message}</Alert>}
            <ConfirmPurgeAllModal opened={purgeAllOpen} onClose={purgeAllH.close}
                title="Очистить весь snapshot-кэш?"
                description="Все snapshot v2 файлы удалятся. При следующем заходе на любой репо они соберутся заново (cold-path)."
                onConfirm={() => purgeAll.mutate()}
                loading={purgeAll.isPending}
            />
        </Stack>
    );
}

function RowActions({ onRebuild, onPurge }: { onRebuild?: () => void; onPurge: () => void }): JSX.Element {
    return (
        <Menu shadow="md" position="bottom-end">
            <Menu.Target>
                <ActionIcon variant="subtle">⋯</ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                {onRebuild && <Menu.Item onClick={onRebuild}>Пересобрать</Menu.Item>}
                <Menu.Item onClick={onPurge} c="red">Удалить</Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
}

// ─── repos ────────────────────────────────────────────────────────────

function ReposTab({ owner, repo }: { owner: string; repo: string }): JSX.Element {
    const qc = useQueryClient();
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const [purgeAllOpen, purgeAllH] = useDisclosure(false);

    const q = useQuery({
        queryKey: ['admin-cache-repos', owner, repo],
        queryFn:  () => fetchReposCache({ owner: owner || undefined, repo: repo || undefined }),
        retry: false,
    });
    const purgeOne = useMutation({
        mutationFn: (e: RepoCheckoutEntry) => purgeReposCache({ owner: e.owner, repo: e.repo, ref: e.ref, kind: e.kind }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-cache-repos'] }),
    });
    const purgeAll = useMutation({
        mutationFn: () => purgeReposCache({ all: true }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-cache-repos'] }); purgeAllH.close(); },
    });

    return (
        <Stack gap="sm">
            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <>
                    <Group justify="space-between" wrap="wrap">
                        <Text fz="sm" c="dimmed">Записей: {q.data.entries.length}, размер: {fmtBytes(q.data.entries.reduce((s, e) => s + e.size, 0))}</Text>
                        <Button color="red" variant="subtle" size="xs" onClick={purgeAllH.open}>Очистить весь раздел</Button>
                    </Group>
                    {isDesktop
                        ? (
                            <Card withBorder padding={0}>
                                <Table fz="sm" striped highlightOnHover verticalSpacing="xs">
                                    <Table.Thead><Table.Tr>
                                        <Table.Th>Owner/Repo</Table.Th>
                                        <Table.Th>Kind</Table.Th>
                                        <Table.Th>Ref</Table.Th>
                                        <Table.Th>Размер</Table.Th>
                                        <Table.Th>Обновлено</Table.Th>
                                        <Table.Th />
                                    </Table.Tr></Table.Thead>
                                    <Table.Tbody>
                                        {q.data.entries.map(e => (
                                            <Table.Tr key={`${e.owner}/${e.repo}/${e.kind}/${e.ref}`}>
                                                <Table.Td>{e.owner}/{e.repo}</Table.Td>
                                                <Table.Td>{e.kind}</Table.Td>
                                                <Table.Td><Text fz="xs" ff="monospace">{e.ref}</Text></Table.Td>
                                                <Table.Td>{fmtBytes(e.size)}</Table.Td>
                                                <Table.Td><Text fz="xs">{fmtRelative(e.mtimeMs)}</Text></Table.Td>
                                                <Table.Td><RowActions onPurge={() => { if (confirm(`Удалить ${e.owner}/${e.repo} ${e.kind}=${e.ref}?`)) purgeOne.mutate(e); }} /></Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Card>
                        )
                        : (
                            <Stack gap="xs">
                                {q.data.entries.map(e => (
                                    <Card withBorder padding="sm" key={`${e.owner}/${e.repo}/${e.kind}/${e.ref}`}>
                                        <Group justify="space-between" align="start" wrap="nowrap">
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <Text fw={500} truncate>{e.owner}/{e.repo}</Text>
                                                <Text fz="xs" c="dimmed" ff="monospace">{e.kind}: {e.ref}</Text>
                                                <Text fz="xs" c="dimmed">{fmtBytes(e.size)} · {fmtRelative(e.mtimeMs)}</Text>
                                            </div>
                                            <RowActions onPurge={() => { if (confirm(`Удалить ${e.owner}/${e.repo} ${e.kind}=${e.ref}?`)) purgeOne.mutate(e); }} />
                                        </Group>
                                    </Card>
                                ))}
                            </Stack>
                        )
                    }
                </>
            )}
            <ConfirmPurgeAllModal opened={purgeAllOpen} onClose={purgeAllH.close}
                title="Удалить все repo-чекауты?"
                description="Все git-чекауты будут удалены. Восстановятся при следующем обращении к репо (медленнее)."
                onConfirm={() => purgeAll.mutate()}
                loading={purgeAll.isPending}
            />
        </Stack>
    );
}

// ─── diffs ────────────────────────────────────────────────────────────

function DiffsTab({ owner, repo }: { owner: string; repo: string }): JSX.Element {
    const qc = useQueryClient();
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const [purgeAllOpen, purgeAllH] = useDisclosure(false);

    const q = useQuery({
        queryKey: ['admin-cache-diffs', owner, repo],
        queryFn:  () => fetchDiffsCache({ owner: owner || undefined, repo: repo || undefined }),
        retry: false,
    });
    const purgeFiltered = useMutation({
        mutationFn: () => purgeDiffsCache({ owner: owner || undefined, repo: repo || undefined }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-cache-diffs'] }),
    });
    const purgeAll = useMutation({
        mutationFn: () => purgeDiffsCache({ all: true }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-cache-diffs'] }); purgeAllH.close(); },
    });

    return (
        <Stack gap="sm">
            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <>
                    <Group justify="space-between" wrap="wrap">
                        <Text fz="sm" c="dimmed">Записей: {q.data.entries.length}, размер: {fmtBytes(q.data.entries.reduce((s, e) => s + e.size, 0))}</Text>
                        <Group gap="xs">
                            {(owner || repo) && (
                                <Button variant="subtle" size="xs" color="red" onClick={() => { if (confirm('Удалить отфильтрованные diff-кэши?')) purgeFiltered.mutate(); }}>
                                    Удалить отфильтрованное
                                </Button>
                            )}
                            <Button color="red" variant="subtle" size="xs" onClick={purgeAllH.open}>Очистить весь раздел</Button>
                        </Group>
                    </Group>
                    {isDesktop
                        ? (
                            <Card withBorder padding={0}>
                                <Table fz="sm" striped highlightOnHover verticalSpacing="xs">
                                    <Table.Thead><Table.Tr>
                                        <Table.Th>Owner/Repo</Table.Th>
                                        <Table.Th>Version</Table.Th>
                                        <Table.Th>Base→Head</Table.Th>
                                        <Table.Th>Размер</Table.Th>
                                        <Table.Th>Обновлено</Table.Th>
                                    </Table.Tr></Table.Thead>
                                    <Table.Tbody>
                                        {q.data.entries.map((e: DiffCacheEntry) => (
                                            <Table.Tr key={`${e.version}/${e.owner}/${e.repo}/${e.baseSha}/${e.headSha}`}>
                                                <Table.Td>{e.owner}/{e.repo}</Table.Td>
                                                <Table.Td>{e.version}</Table.Td>
                                                <Table.Td><Text fz="xs" ff="monospace">{e.baseSha.slice(0, 7)} → {e.headSha.slice(0, 7)}</Text></Table.Td>
                                                <Table.Td>{fmtBytes(e.size)}</Table.Td>
                                                <Table.Td><Text fz="xs">{fmtRelative(e.mtimeMs)}</Text></Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Card>
                        )
                        : (
                            <Stack gap="xs">
                                {q.data.entries.map(e => (
                                    <Card withBorder padding="sm" key={`${e.version}/${e.owner}/${e.repo}/${e.baseSha}/${e.headSha}`}>
                                        <Text fw={500} truncate>{e.owner}/{e.repo}</Text>
                                        <Text fz="xs" c="dimmed" ff="monospace">{e.baseSha.slice(0, 7)} → {e.headSha.slice(0, 7)} · {e.version}</Text>
                                        <Text fz="xs" c="dimmed">{fmtBytes(e.size)} · {fmtRelative(e.mtimeMs)}</Text>
                                    </Card>
                                ))}
                            </Stack>
                        )
                    }
                </>
            )}
            <ConfirmPurgeAllModal opened={purgeAllOpen} onClose={purgeAllH.close}
                title="Удалить весь diff-кэш?"
                description="Все агрегированные diff'ы будут удалены. Compare-страница пересоберёт нужные diff'ы по требованию (с LLM-narrative — это дороже)."
                onConfirm={() => purgeAll.mutate()}
                loading={purgeAll.isPending}
            />
        </Stack>
    );
}

function ConfirmPurgeAllModal({ opened, onClose, title, description, onConfirm, loading }: {
    opened: boolean; onClose: () => void; title: string; description: string;
    onConfirm: () => void; loading: boolean;
}): JSX.Element {
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    return (
        <Modal opened={opened} onClose={onClose} fullScreen={!isDesktop} title={title} centered>
            <Stack>
                <Text>{description}</Text>
                <Group justify="end">
                    <Button variant="default" onClick={onClose}>Отмена</Button>
                    <Button color="red" loading={loading} onClick={onConfirm}>Очистить</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
